// Per-CLIN deliverable schedule for the CLIN detail drawer, on the contract
// model (D112 #6): ListOccurrences{clin_id} — the dates minted by a rule on
// this CLIN, plus one-offs of a deliverable with such a rule — grouped by
// deliverable, expandable to its dates. Each date's status and label come
// from lib/status, so it reads here as it does on Deliverables; each date
// links to its drawer.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraDataTable, SpecraEmptyState, SpecraInlineBanner, SpecraLink, SpecraSpinner, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { CmStatusBadge } from '../../Deliverables/components/common';
import { OccurrenceWindow, useDuties, useOccurrences } from '../../Deliverables/hooks';
import { cmDate } from '../../Deliverables/lib/format';
import { occurrenceStatus } from '../../Deliverables/lib/status';
// Dates that no longer owe anything: approved, waived by the COR, not owed.
const SETTLED = new Set([
    'approved',
    'waived',
    'not_owed'
]);
export function ClinOccurrenceSchedule({ programId, clinId }) {
    const occ = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        clinId
    }, {
        enabled: !!clinId
    });
    const duties = useDuties(programId, {
        includeRules: true
    });
    const dutyById = useMemo(()=>new Map((duties.data?.duties ?? []).map((d)=>[
                d.id,
                d
            ])), [
        duties.data
    ]);
    const [open, setOpen] = useState(null);
    const groups = useMemo(()=>{
        const byDuty = new Map();
        for (const o of occ.data?.occurrences ?? []){
            const g = byDuty.get(o.dutyId) ?? {
                dutyId: o.dutyId,
                dates: [],
                settled: 0,
                late: 0
            };
            g.dates.push(o);
            if (SETTLED.has(o.status?.state ?? '')) g.settled += 1;
            if (o.status?.state === 'late') g.late += 1;
            byDuty.set(o.dutyId, g);
        }
        const title = (id)=>dutyById.get(id)?.title ?? '';
        for (const g of byDuty.values())g.dates.sort((a, b)=>(a.dueOn || '9999').localeCompare(b.dueOn || '9999'));
        return [
            ...byDuty.values()
        ].sort((a, b)=>title(a.dutyId).localeCompare(title(b.dutyId)));
    }, [
        occ.data,
        dutyById
    ]);
    const deliverablesHref = `/delivery/programs/${programId}/deliverables`;
    const columns = [
        {
            id: 'ref',
            header: 'Ref',
            className: 'font-mono',
            cell: ({ group, date })=>{
                if (date) return null;
                const duty = dutyById.get(group.dutyId);
                return duty?.letter || duty?.pwsRef || '';
            }
        },
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: ({ group, date })=>{
                if (date) {
                    return /*#__PURE__*/ _jsxs(SpecraLink, {
                        className: "tabular-nums",
                        href: `${deliverablesHref}?occ=${date.id}`,
                        renderLink: renderRouterLink,
                        children: [
                            cmDate(date.dueOn),
                            date.label ? /*#__PURE__*/ _jsx("span", {
                                className: "ml-1.5 text-muted-foreground",
                                children: date.label
                            }) : null
                        ]
                    });
                }
                const duty = dutyById.get(group.dutyId);
                return /*#__PURE__*/ _jsx(SpecraLink, {
                    className: "block max-w-[14rem] truncate",
                    href: `${deliverablesHref}/${group.dutyId}`,
                    renderLink: renderRouterLink,
                    title: duty?.title,
                    children: duty?.title || 'Untitled deliverable'
                });
            }
        },
        {
            id: 'status',
            header: 'Status',
            align: 'right',
            cell: ({ group, date })=>{
                if (date) {
                    return /*#__PURE__*/ _jsx(CmStatusBadge, {
                        status: occurrenceStatus(date, dutyById.get(group.dutyId)?.finalReviewTier)
                    });
                }
                return /*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-1",
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraBadge, {
                            variant: group.settled === group.dates.length ? 'success' : 'neutral',
                            children: [
                                group.settled,
                                "/",
                                group.dates.length,
                                " done"
                            ]
                        }),
                        group.late > 0 && /*#__PURE__*/ _jsxs(SpecraBadge, {
                            variant: "danger",
                            children: [
                                group.late,
                                " late"
                            ]
                        })
                    ]
                });
            }
        }
    ];
    if (occ.isLoading) {
        return /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex items-center gap-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraSpinner, {
                    label: false,
                    size: "sm"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "body-ui",
                    tone: "muted",
                    children: "Loading deliverable dates…"
                })
            ]
        });
    }
    if (occ.isError) {
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            tone: "danger",
            variant: "framed",
            children: "Couldn't load this CLIN's dates."
        });
    }
    if (groups.length === 0) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            action: /*#__PURE__*/ _jsx(SpecraLink, {
                href: deliverablesHref,
                renderLink: renderRouterLink,
                children: "Open Deliverables"
            }),
            size: "compact",
            title: "No deliverable dates are scheduled on this CLIN."
        });
    }
    return /*#__PURE__*/ _jsx(SpecraDataTable, {
        columns: columns,
        "data-testid": Testids.deliveryOperational.clinDetailDeliverables,
        expandedIds: open ? [
            open
        ] : [],
        framed: true,
        getExpandToggleLabel: ({ group }, isOpen)=>`${isOpen ? 'Hide' : 'Show'} dates for ${dutyById.get(group.dutyId)?.title || 'Untitled deliverable'}`,
        getRowChildren: (r)=>r.date ? undefined : r.group.dates.map((d)=>({
                    group: r.group,
                    date: d
                })),
        getRowId: (r)=>r.date ? `${r.group.dutyId}/${r.date.id}` : r.group.dutyId,
        getRowProps: (r)=>r.date ? {} : {
                className: 'cursor-pointer',
                onClick: (e)=>{
                    if (e.target.closest('a, button')) return;
                    setOpen((c)=>c === r.group.dutyId ? null : r.group.dutyId);
                }
            },
        onExpandedChange: (ids)=>{
            const opened = ids.find((id)=>id !== open);
            setOpen(opened === undefined ? null : String(opened));
        },
        rows: groups.map((group)=>({
                group
            }))
    });
}
